import React from "react";
import AliceCarousel from "react-alice-carousel";
import "react-alice-carousel/lib/alice-carousel.css";
import { useNavigate } from "react-router-dom";
import useSiteSettings from "../../../hooks/useSiteSettings";
import useMediaQuery from "@mui/material/useMediaQuery";

const handleDragStart = (e) => e.preventDefault();

const HomeCarousel = () => {
  const navigate = useNavigate();
  const settings = useSiteSettings();
  const brand = settings.brandName || "";

  // Hero slides are admin-managed only — no hardcoded fallback. If the admin
  // hasn't added any banners, the hero simply doesn't render.
  const slides = settings.heroSlides || [];
  // Phones get the admin's portrait "phone image" when slides have one: a wide
  // banner shrunk to phone width made its text unreadable. Hooks run before
  // the early return below.
  const isPhone = useMediaQuery("(max-width: 639.95px)");
  if (!slides.length) return null;
  const phoneMode = isPhone && slides.some((sl) => sl.mobileImage);

  // Each slide is a box of fixed ASPECT holding the whole, uncropped banner.
  //
  // The size lives on this box rather than on the image via h-full, because
  // AliceCarousel wraps every item in auto-height divs — h-full would resolve
  // to auto and the image would render at its natural aspect ratio (a 3:4
  // phone photo becomes a ~1920px-tall banner on desktop).
  //
  // It's an aspect ratio and NOT a vh height: a vh box keeps the same height
  // while the screen narrows, so its shape swung from 2.6:1 on desktop to
  // 0.9:1 on a phone. Banners are wide, so object-contain then fitted them to
  // the width and left the remaining ~67% of a phone hero as blurred backdrop.
  // 8/3 (2.67:1) matches the banner format, so the image fills the box on every
  // screen. Desktop is unaffected — 62vh at 1440px was 558px, this is 540px.
  //
  // object-contain still keeps the full frame visible, so nothing is cropped
  // off. An off-format upload is smaller than the box in one axis, so the same
  // image is repeated behind it, blurred and over-scaled, to fill the leftover
  // space instead of leaving empty bars.
  const items = slides.map((item, i) => {
    const src = phoneMode && item.mobileImage ? item.mobileImage : item.image;
    return (
    <div
      key={i}
      /* No min-height: the box is EXACTLY the banner ratio, so the image fills
         it edge to edge at every width and the blurred backdrop never shows.
         A min-height taller than width/2.67 reappears as empty bands above and
         below the picture — it only existed to hold space for the old text
         overlay, which no longer renders. Re-add it if that overlay returns. */
      className={`relative ${phoneMode ? "aspect-[4/5]" : "aspect-[8/3]"} max-h-[680px] w-full overflow-hidden bg-[#2a1810]`}
      onClick={() => item.link && navigate(item.link)}
      role="presentation"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 scale-110 bg-cover bg-center blur-xl"
        style={{ backgroundImage: `url(${src})` }}
      />
      <img
        className="relative mx-auto h-full w-auto max-w-full cursor-pointer object-contain"
        src={src}
        alt={brand}
        onDragStart={handleDragStart}
      />
    </div>
    );
  });

  return (
    <div className="relative">
      <AliceCarousel
        mouseTracking
        items={items}
        autoPlay
        infinite
        autoPlayInterval={3000}
        animationDuration={900}
        disableButtonsControls
        disableDotsControls
      />
    </div>
  );
};

export default HomeCarousel;
